import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Panel } from "./panel";
import { Pager } from "./pager";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { hasTier } from "./access-control";
import { apiErrorMessage } from "../../pages/PortfolioPerformance/lib/api/exposure-tracking";
import {
  useApdGrid,
  useApdGridLog,
  useDecideApdGrid,
  useProposeApdGrid,
  type ApdGridBand,
  type ApdGridProposal,
} from "../../pages/PortfolioPerformance/lib/api/attestation";

const dateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "-");

const STATUS_CLASS: Record<ApdGridProposal["status"], string> = {
  pending: "text-amber",
  approved: "text-approve",
  rejected: "text-decline",
};

// The grid stores each limit as a fraction (0.0375); people read and type it as a percentage (3.75%).
const toPct = (fraction: number) => Number((fraction * 100).toFixed(4));
const fromPct = (pct: number) => Number((pct / 100).toFixed(6));
const pct = (fraction: number) => `${toPct(fraction)}%`;

const range = (b: ApdGridBand) => `${pct(b.lowerLimit)} to ${pct(b.upperLimit)}`;

// A row is identified by its sub-category (the grid's key); older proposals have none and read as their category.
const subOf = (b: ApdGridBand) => b.subCategory || b.category;
const label = (b: ApdGridBand) => (subOf(b) === b.category ? b.category : `${subOf(b)} (${b.category})`);

/** One line per sub-category that changed, was added, or was removed (matched by sub-category name). */
function describeChange(current: ApdGridBand[], proposed: ApdGridBand[]): string[] {
  const before = new Map(current.map((b) => [subOf(b), b]));
  const after = new Map(proposed.map((b) => [subOf(b), b]));
  const lines: string[] = [];
  for (const b of proposed) {
    const old = before.get(subOf(b));
    if (!old) lines.push(`+ ${label(b)}: ${range(b)}`);
    else if (old.category !== b.category) lines.push(`${label(old)} → ${label(b)}: ${range(b)}`);
    else if (old.lowerLimit !== b.lowerLimit || old.upperLimit !== b.upperLimit)
      lines.push(`${label(b)}: ${range(old)} → ${range(b)}`);
  }
  for (const b of current) if (!after.has(subOf(b))) lines.push(`- ${label(b)}: ${range(b)}`);
  return lines;
}

type DraftBand = { category: string; subCategory: string; lowerLimit: string; upperLimit: string };

function ProposeEditor({
  current,
  disabled,
  onDone,
}: {
  current: ApdGridBand[];
  disabled: boolean;
  onDone: () => void;
}) {
  const propose = useProposeApdGrid();
  const [rows, setRows] = useState<DraftBand[]>(() =>
    current.map((b) => ({
      category: b.category,
      subCategory: subOf(b),
      lowerLimit: String(toPct(b.lowerLimit)),
      upperLimit: String(toPct(b.upperLimit)),
    })),
  );
  const [reason, setReason] = useState("");

  const parsed = rows.map((r) => ({
    category: r.category,
    subCategory: r.subCategory.trim(),
    lowerLimit: Number(r.lowerLimit),
    upperLimit: Number(r.upperLimit),
  }));
  const valid =
    rows.length > 0 &&
    reason.trim() !== "" &&
    rows.every((r, i) => r.category.trim() !== "" && r.subCategory.trim() !== "" && r.lowerLimit.trim() !== "" && r.upperLimit.trim() !== "" &&
      Number.isFinite(parsed[i]!.lowerLimit) && Number.isFinite(parsed[i]!.upperLimit));

  function update(i: number, patch: Partial<DraftBand>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  return (
    <form
      className="mt-5 grid max-w-3xl gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || disabled) return;
        propose.mutate(
          {
            bands: parsed.map((r) => ({ ...r, lowerLimit: fromPct(r.lowerLimit), upperLimit: fromPct(r.upperLimit) })),
            reason: reason.trim(),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="grid gap-2">
        <div className="grid grid-cols-[1fr_1fr_8rem_8rem_2.25rem] gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span>Category</span>
          <span>Sub Category</span>
          <span>From (incl.) %</span>
          <span>To (excl.) %</span>
          <span />
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_8rem_8rem_2.25rem] gap-2">
            <Input
              value={r.category}
              onChange={(e) => update(i, { category: e.target.value })}
              aria-label="Category"
              disabled={disabled}
            />
            <Input
              value={r.subCategory}
              onChange={(e) => update(i, { subCategory: e.target.value })}
              aria-label="Sub Category"
              disabled={disabled}
            />
            <Input
              inputMode="decimal"
              value={r.lowerLimit}
              onChange={(e) => update(i, { lowerLimit: e.target.value })}
              aria-label="From"
              disabled={disabled}
            />
            <Input
              inputMode="decimal"
              value={r.upperLimit}
              onChange={(e) => update(i, { upperLimit: e.target.value })}
              aria-label="To"
              disabled={disabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove row"
              disabled={disabled || rows.length === 1}
              onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={disabled}
            onClick={() =>
              setRows((prev) => [...prev, { category: "", subCategory: "", lowerLimit: "", upperLimit: "" }])
            }
          >
            <Plus className="size-3.5" />
            Add category
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Rows must not overlap or leave gaps: each one starts where the previous one ends. Each sub-category appears once; several can share a category.
      </p>
      <label className="grid gap-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Reason
        <Textarea
          placeholder="Why are the categories changing?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={disabled}
        />
      </label>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!valid || disabled || propose.isPending}>
          {propose.isPending ? "Submitting…" : "Submit for approval"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        {propose.isError ? (
          <span className="text-xs text-destructive">{apiErrorMessage(propose.error)}</span>
        ) : null}
      </div>
    </form>
  );
}

function GridPanel() {
  const gridQ = useApdGrid();
  const canPropose = hasTier("reviewer");
  const [editing, setEditing] = useState(false);
  const pending = gridQ.data?.hasPending ?? false;

  return (
    <Panel
      className="mt-6"
      title="APD Category Configuration"
      description="The APD percentage range behind each category (from is inclusive, to is exclusive), used to categorize contracts. A change only takes effect once a contributor approves it."
      right={
        canPropose && gridQ.data && !editing ? (
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setEditing(true)}>
            Propose changes
          </Button>
        ) : null
      }
    >
      {gridQ.isError ? (
        <p className="text-sm text-destructive">Could not load the APD categories.</p>
      ) : !gridQ.data ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          <table className="w-full max-w-3xl text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="pb-2">Category</th>
                <th className="pb-2">Sub Category</th>
                <th className="pb-2 text-right">From</th>
                <th className="pb-2 text-right">To</th>
              </tr>
            </thead>
            <tbody>
              {gridQ.data.bands.map((b) => (
                <tr key={subOf(b)} className="border-t border-border">
                  <td className="py-2">{b.category}</td>
                  <td className="py-2">{subOf(b)}</td>
                  <td className="py-2 text-right tabular-nums">{pct(b.lowerLimit)}</td>
                  <td className="py-2 text-right tabular-nums">{pct(b.upperLimit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {pending ? (
            <p className="mt-3 text-xs text-amber">
              A proposal is pending approval. Decide it in the log below before proposing another.
            </p>
          ) : null}
          {!canPropose ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Only reviewers and above can propose a change to the categories.
            </p>
          ) : null}
          {editing ? (
            <ProposeEditor current={gridQ.data.bands} disabled={pending} onDone={() => setEditing(false)} />
          ) : null}
        </>
      )}
    </Panel>
  );
}

function DecisionCell({ proposal }: { proposal: ApdGridProposal }) {
  const decide = useDecideApdGrid();
  const [notes, setNotes] = useState("");
  return (
    <div className="grid min-w-[200px] gap-2">
      <Input
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={decide.isPending}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ id: proposal.id, status: "approved", notes })}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={decide.isPending}
          onClick={() => decide.mutate({ id: proposal.id, status: "rejected", notes })}
        >
          Reject
        </Button>
      </div>
      {decide.isError ? (
        <span className="text-xs text-destructive">{apiErrorMessage(decide.error)}</span>
      ) : null}
    </div>
  );
}

function GridLog() {
  const [offset, setOffset] = useState(0);
  const logQ = useApdGridLog(offset);
  const canApprove = hasTier("contributor");

  return (
    <Panel
      className="mt-6"
      title="APD Category Change Log"
      description="Every proposed change to the APD categories, newest first, with who proposed it and who approved or rejected it."
    >
      {logQ.isError ? (
        <p className="text-sm text-destructive">Could not load the proposal log.</p>
      ) : !logQ.data ? (
        <Skeleton className="h-32 w-full" />
      ) : logQ.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No proposals yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="pb-2">Proposed</th>
                  <th className="pb-2">Change</th>
                  <th className="pb-2">Reason</th>
                  <th className="pb-2">Proposed By</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Reviewed</th>
                  <th className="pb-2">Notes</th>
                  {canApprove ? <th className="pb-2">Decision</th> : null}
                </tr>
              </thead>
              <tbody>
                {logQ.data.items.map((p) => {
                  const changes = describeChange(p.currentGrid, p.proposedGrid);
                  return (
                    <tr key={p.id} className="border-t border-border align-top">
                      <td className="py-2 pr-3 text-muted-foreground">{dateTime(p.proposedAt)}</td>
                      <td className="py-2 pr-3 tabular-nums">
                        {changes.length === 0 ? (
                          <span className="text-muted-foreground">No change</span>
                        ) : (
                          changes.map((line) => <div key={line}>{line}</div>)
                        )}
                      </td>
                      <td className="py-2 pr-3">{p.reason}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{p.proposedBy ?? "-"}</td>
                      <td className={`py-2 pr-3 font-medium capitalize ${STATUS_CLASS[p.status]}`}>
                        {p.status}
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {p.reviewedBy ? `${p.reviewedBy} · ${dateTime(p.reviewedAt)}` : "-"}
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">{p.reviewNotes ?? ""}</td>
                      {canApprove ? (
                        <td className="py-2">
                          {p.status === "pending" ? <DecisionCell proposal={p} /> : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager
            offset={offset}
            total={logQ.data.total}
            isFetching={logQ.isFetching}
            onChange={setOffset}
          />
        </>
      )}
    </Panel>
  );
}

export function ApdGridAttestationSection() {
  return (
    <>
      <GridPanel />
      <GridLog />
    </>
  );
}
